import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { checkout, getJwt, type CheckoutPlan } from "../lib/api";
import { MBBtn, MBChip, MBDot, MBEyebrow, MBIcon, MBWordmark } from "../components/mb";
import { MartzmakesWordmark } from "../components/MartzmakesWordmark";

const MAX = 1200;

export function Landing() {
  return (
    <div data-screen-label="Marketing — Landing">
      <Nav />
      <main>
        <Hero />
        <HowItWorks />
        <Features />
        <UseCases />
        <Pricing />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

// ── Nav ─────────────────────────────────────────────────────────────────────
function Nav() {
  const signedIn = !!getJwt();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        height: 56,
        borderBottom: scrolled ? "1px solid var(--line-2)" : "1px solid transparent",
        background: scrolled ? "rgba(7,9,12,0.92)" : "rgba(7,9,12,0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        transition: "background 200ms, border-color 200ms",
      }}
    >
      <div
        style={{
          maxWidth: MAX,
          margin: "0 auto",
          height: "100%",
          padding: "0 32px",
          display: "flex",
          alignItems: "center",
          gap: 32,
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
          <MBWordmark size={18} />
        </Link>
        <nav
          style={{
            display: "flex",
            gap: 24,
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            whiteSpace: "nowrap",
          }}
          className="max-sm:hidden"
        >
          <a style={{ color: "var(--fg-2)" }} href="#how">How it works</a>
          <a style={{ color: "var(--fg-2)" }} href="#features">Features</a>
          <a style={{ color: "var(--fg-2)" }} href="#pricing">Pricing</a>
          <Link style={{ color: "var(--fg-2)" }} to="/docs">Docs</Link>
          <a style={{ color: "var(--fg-2)" }} href="#faq">FAQ</a>
        </nav>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          {signedIn ? (
            <Link to="/projects">
              <MBBtn variant="primary" size="sm" icon={<MBIcon name="arrow" size={14} />}>
                Open app
              </MBBtn>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <MBBtn variant="ghost" size="sm">Sign in</MBBtn>
              </Link>
              <Link to="/login">
                <MBBtn variant="primary" size="sm" icon={<MBIcon name="arrow" size={14} />}>
                  Start free
                </MBBtn>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ── Video frame ──────────────────────────────────────────────────────────────
function VideoFrame({
  src,
  label,
  accent = false,
  height = 360,
  preload = "metadata",
}: {
  src: string;
  label?: string;
  accent?: boolean;
  height?: number;
  preload?: "none" | "metadata" | "auto";
}) {
  return (
    <div
      style={{
        background: "var(--surface-1)",
        border: `1px solid ${accent ? "var(--accent-line)" : "var(--line-2)"}`,
        borderRadius: 12,
        boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 60px rgba(0,0,0,0.55)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 14px",
          borderBottom: "1px solid var(--line-1)",
          gap: 10,
          background: "var(--surface-1)",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--surface-4)" }} />
          ))}
        </div>
        <div style={{ marginLeft: 8, fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)" }}>
          {label || "mcpbuild.app"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <MBDot color="var(--success)" pulse />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)" }}>live</span>
        </div>
      </div>
      <div style={{ position: "relative", background: "var(--bg-0)" }}>
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload={preload}
          style={{ display: "block", width: "100%", height, objectFit: "cover", background: "var(--bg-0)" }}
        />
      </div>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const signedIn = !!getJwt();
  const scrollHow = () =>
    document.getElementById("how")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <section style={{ position: "relative", overflow: "hidden" }}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 70% 55% at 50% 30%, #000 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 55% at 50% 30%, #000 30%, transparent 75%)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -120,
          left: "50%",
          transform: "translateX(-50%)",
          width: 800,
          height: 320,
          background: "radial-gradient(ellipse at center, rgba(59,130,255,0.10), transparent 60%)",
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", maxWidth: MAX, margin: "0 auto", padding: "88px 32px 56px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <a
            href="#how"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 14px",
              background: "var(--surface-1)",
              border: "1px solid var(--line-2)",
              borderRadius: 999,
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              letterSpacing: "0.04em",
              color: "var(--fg-2)",
              textTransform: "lowercase",
            }}
          >
            <MBDot color="var(--accent)" pulse />
            now live in claude &amp; chatgpt
          </a>
        </div>

        <h1
          style={{
            textAlign: "center",
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(40px, 8vw, 68px)",
            lineHeight: 1.06,
            letterSpacing: "-0.035em",
            fontWeight: 600,
            color: "var(--fg-1)",
            textWrap: "balance",
          }}
        >
          Chat to a tool.
          <br />
          <span style={{ color: "var(--fg-3)" }}>Live in your MCP client.</span>
        </h1>

        <p
          style={{
            textAlign: "center",
            maxWidth: 640,
            margin: "22px auto 0",
            fontFamily: "var(--font-sans)",
            fontSize: 18,
            lineHeight: "28px",
            color: "var(--fg-2)",
            letterSpacing: "-0.01em",
          }}
        >
          Ask Claude or ChatGPT to build a widget inside MCPBuild. Each project becomes a
          connectable MCP server — same widget, both clients, with live data and version history.
        </p>

        <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to={signedIn ? "/projects" : "/login"}>
            <MBBtn variant="primary" size="lg" icon={<MBIcon name="arrow" size={15} />}>
              {signedIn ? "Open your projects" : "Start free"}
            </MBBtn>
          </Link>
          <MBBtn variant="outline" size="lg" icon={<MBIcon name="play" size={12} />} onClick={scrollHow}>
            Watch the 60-sec tour
          </MBBtn>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "center",
            gap: 20,
            flexWrap: "wrap",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--fg-4)",
            textTransform: "lowercase",
            letterSpacing: "0.04em",
          }}
        >
          {["free forever tier", "no credit card", "ships in 60 seconds"].map((t, i) => (
            <span key={t} style={{ display: "inline-flex", gap: 14, alignItems: "center" }}>
              {i > 0 && <span style={{ color: "var(--fg-5)" }}>·</span>}
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <MBIcon name="check" size={12} color="var(--success)" /> {t}
              </span>
            </span>
          ))}
        </div>

        <div style={{ marginTop: 56 }}>
          <VideoFrame src="/demos/create-widget.mp4" label="mcpbuild.app / new" accent height={520} />
        </div>

        {/* Trust row — two tiers: where it renders vs. where you can connect it. */}
        <div style={{ marginTop: 64, paddingTop: 32, borderTop: "1px solid var(--line-1)" }}>
          <ClientTier label="renders inline in" names={["Claude", "ChatGPT"]} size={18} opacity={0.9} />
          <ClientTier
            label="connect the MCP from any client"
            names={["Cursor", "Zed", "any MCP client"]}
            size={15}
            opacity={0.6}
            style={{ marginTop: 32 }}
          />
        </div>
      </div>
    </section>
  );
}

function ClientTier({
  label,
  names,
  size,
  opacity,
  style = {},
}: {
  label: string;
  names: string[];
  size: number;
  opacity: number;
  style?: CSSProperties;
}) {
  return (
    <div style={style}>
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: "0.08em", color: "var(--fg-4)", textTransform: "uppercase" }}>
          {label}
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 44, flexWrap: "wrap", opacity }}>
        {names.map((n, i) => (
          <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 44 }}>
            {i > 0 && <span style={{ color: "var(--fg-5)" }}>·</span>}
            <span style={{ fontFamily: "var(--font-sans)", fontSize: size, fontWeight: 600, color: "var(--fg-3)", letterSpacing: "-0.02em" }}>{n}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── How it works ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    {
      n: "01",
      label: "build",
      title: "Chat your way to a widget.",
      body: "Describe what you want in plain English. Claude or ChatGPT writes the React, fetches real data, and renders it inline — right inside MCPBuild.",
      video: "/demos/create-widget.mp4",
      vlabel: "mcpbuild.app / new",
      chip: "30 seconds",
      icon: "chat",
      extra: <ExamplePrompt />,
    },
    {
      n: "02",
      label: "connect",
      title: "Every project is an MCP endpoint.",
      body: "Each project comes with its own MCP server URL. Add it once to Claude, Cursor, or any MCP client — your widget is available as a callable tool.",
      video: "/demos/connect-mcp.mp4",
      vlabel: "mcpbuild.app / project / connect",
      chip: "1 click",
      icon: "link",
      extra: <MCPConfigSnippet />,
    },
    {
      n: "03",
      label: "share",
      title: "Public widgets, no login required.",
      body: "Flip the toggle to make a project public. Share a URL — anyone can use the widget directly in ChatGPT or open it as a hosted page.",
      video: "/demos/chatgpt-public.mp4",
      vlabel: "chatgpt.com",
      chip: "shareable link",
      icon: "share",
      extra: <ShareLinkRow />,
    },
  ];

  return (
    <section
      id="how"
      style={{ maxWidth: MAX, margin: "0 auto", padding: "96px 32px 32px", borderTop: "1px solid var(--line-1)", scrollMarginTop: 56 }}
    >
      <div style={{ textAlign: "center", marginBottom: 64 }}>
        <MBEyebrow style={{ marginBottom: 14 }}>How it works</MBEyebrow>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 44,
            lineHeight: "48px",
            letterSpacing: "-0.025em",
            fontWeight: 600,
            color: "var(--fg-1)",
            margin: "0 auto",
            maxWidth: 720,
            textWrap: "balance",
          }}
        >
          Three steps, end-to-end.
          <br />
          <span style={{ color: "var(--fg-3)" }}>No infra, no glue code, no marketplaces.</span>
        </h2>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 88 }}>
        {steps.map((s, i) => (
          <div key={s.n} className="mb-step" style={{ ["--rev" as string]: i % 2 === 1 ? "1" : "0" }}>
            <div className="mb-step-text">
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 52,
                    fontWeight: 600,
                    color: "var(--surface-4)",
                    letterSpacing: "-0.04em",
                    lineHeight: 1,
                  }}
                >
                  {s.n}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11.5,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--accent-fg)",
                    }}
                  >
                    {s.label}
                  </span>
                  <MBChip>
                    <MBIcon name={s.icon} size={11} /> {s.chip}
                  </MBChip>
                </div>
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "var(--font-sans)",
                  fontSize: 32,
                  lineHeight: "36px",
                  letterSpacing: "-0.02em",
                  fontWeight: 600,
                  color: "var(--fg-1)",
                  textWrap: "balance",
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  marginTop: 14,
                  fontFamily: "var(--font-sans)",
                  fontSize: 16,
                  lineHeight: "26px",
                  color: "var(--fg-2)",
                  maxWidth: 460,
                  letterSpacing: "-0.01em",
                }}
              >
                {s.body}
              </p>
              {s.extra}
            </div>
            <div className="mb-step-media">
              <VideoFrame src={s.video} label={s.vlabel} height={400} preload="none" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ExamplePrompt() {
  return (
    <div
      style={{
        marginTop: 22,
        padding: "12px 16px",
        background: "var(--surface-1)",
        border: "1px solid var(--line-2)",
        borderRadius: 8,
        maxWidth: 460,
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        lineHeight: "20px",
        color: "var(--fg-2)",
      }}
    >
      <span style={{ color: "var(--fg-4)" }}>{"> "}</span>
      Build me a quarterly revenue chart from
      <span style={{ color: "var(--accent-fg)" }}> analytics.acme.com/api</span>, grouped by product line.
    </div>
  );
}

function MCPConfigSnippet() {
  return (
    <div
      style={{
        marginTop: 22,
        maxWidth: 460,
        background: "var(--surface-1)",
        border: "1px solid var(--line-2)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 14px",
          borderBottom: "1px solid var(--line-1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)" }}>~/.claude/mcp.json</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)", display: "inline-flex", gap: 4, alignItems: "center" }}>
          <MBIcon name="copy" size={11} /> copy
        </span>
      </div>
      <pre style={{ margin: 0, padding: "12px 14px", fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: "18px", color: "var(--fg-1)", background: "transparent" }}>
        <span style={{ color: "var(--fg-3)" }}>{"{"}</span>
        {"\n  "}
        <span style={{ color: "var(--accent-fg)" }}>"mcpbuild"</span>
        <span style={{ color: "var(--fg-3)" }}>: {"{"}</span>
        {"\n    "}
        <span style={{ color: "var(--accent-fg)" }}>"url"</span>:{" "}
        <span style={{ color: "var(--success)" }}>"https://mcpbuild.app/mcp/quarterly-revenue"</span>
        {"\n  "}
        <span style={{ color: "var(--fg-3)" }}>{"}"}</span>
        {"\n"}
        <span style={{ color: "var(--fg-3)" }}>{"}"}</span>
      </pre>
    </div>
  );
}

function ShareLinkRow() {
  return (
    <div
      style={{
        marginTop: 22,
        display: "flex",
        alignItems: "stretch",
        maxWidth: 460,
        background: "var(--surface-1)",
        border: "1px solid var(--line-2)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-2)" }}>
        <MBIcon name="globe" size={13} color="var(--accent-fg)" />
        mcpbuild.app/p/<span style={{ color: "var(--fg-1)" }}>quarterly-revenue</span>
      </div>
      <span
        style={{
          padding: "0 16px",
          borderLeft: "1px solid var(--line-2)",
          background: "var(--surface-2)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <MBIcon name="copy" size={12} /> Copy
      </span>
    </div>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
function Features() {
  const feats = [
    { icon: "zap", label: "live data", title: "Real APIs, not fakes.", body: "Widgets call your endpoints with credentials scoped to the project, injected server-side. No copy-pasted JSON, no demo data, no leaked keys." },
    { icon: "history", label: "versioning", title: "Every save is a checkpoint.", body: "Remix a widget in chat and it saves a new version. Roll back to any earlier version in one click — full history is kept." },
    { icon: "bot", label: "two clients, one widget", title: "Claude and ChatGPT, in parity.", body: "Build it in either chat client. Connect it as MCP. Same component, same behavior — no separate forks." },
    { icon: "layers", label: "mcp-native", title: "Each project is an endpoint.", body: "Projects come pre-wired as MCP servers. Add one URL to your client config — every tool inside becomes callable." },
    { icon: "share", label: "public sharing", title: "A link is enough.", body: "Flip a project public. Drop the URL into a ChatGPT conversation — your widget renders inline. No installer, no auth wall." },
    { icon: "lock", label: "private by default", title: "Private until you say otherwise.", body: "Projects start private and stay that way. Per-project, KMS-encrypted secrets and a fetch allowlist on the Pro plan." },
  ];

  return (
    <section
      id="features"
      style={{ maxWidth: MAX, margin: "0 auto", padding: "96px 32px", borderTop: "1px solid var(--line-1)", scrollMarginTop: 56 }}
    >
      <div style={{ maxWidth: 640, marginBottom: 56 }}>
        <MBEyebrow style={{ marginBottom: 14 }}>What's in the box</MBEyebrow>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 40,
            lineHeight: "44px",
            letterSpacing: "-0.025em",
            fontWeight: 600,
            color: "var(--fg-1)",
            margin: 0,
            textWrap: "balance",
          }}
        >
          Everything you need to ship a tool — nothing you don't.
        </h2>
      </div>

      <div className="mb-feature-grid" style={{ border: "1px solid var(--line-2)", borderRadius: 12, overflow: "hidden", background: "var(--surface-1)" }}>
        {feats.map((f, i) => (
          <div
            key={f.label}
            className="mb-feature-cell"
            style={{ ["--i" as string]: String(i), padding: "28px 26px", display: "flex", flexDirection: "column", gap: 12 }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--surface-2)",
                border: "1px solid var(--line-2)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--accent-fg)",
              }}
            >
              <MBIcon name={f.icon} size={18} />
            </div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10.5,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-4)",
                marginTop: 4,
              }}
            >
              {f.label}
            </span>
            <h4 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 18, lineHeight: "24px", letterSpacing: "-0.01em", fontWeight: 600, color: "var(--fg-1)" }}>
              {f.title}
            </h4>
            <p style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: "22px", color: "var(--fg-2)" }}>{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Use cases (illustrative) ─────────────────────────────────────────────────
function UseCases() {
  const cases = [
    { title: "Quarterly Revenue", who: "finance", colors: ["#3B82FF", "#3DCF8E", "#E9B949"] },
    { title: "Customer Health Score", who: "success", colors: ["#3DCF8E", "#3B82FF"] },
    { title: "Sprint Standup Brief", who: "engineering", colors: ["#3B82FF"] },
    { title: "On-call Status Board", who: "platform", colors: ["#F26D6D", "#E9B949", "#3DCF8E"] },
    { title: "Pricing Calculator", who: "sales", colors: ["#E9B949"] },
    { title: "Active Incidents", who: "support", colors: ["#F26D6D"] },
  ];
  return (
    <section style={{ maxWidth: MAX, margin: "0 auto", padding: "32px 32px 96px" }}>
      <div style={{ marginBottom: 32 }}>
        <MBEyebrow style={{ marginBottom: 14 }}>What people build</MBEyebrow>
        <h2 style={{ fontFamily: "var(--font-sans)", fontSize: 32, lineHeight: "36px", letterSpacing: "-0.02em", fontWeight: 600, color: "var(--fg-1)", margin: 0 }}>
          From dashboards to one-off calculators.
        </h2>
      </div>
      <div className="mb-usecase-grid">
        {cases.map((c, i) => (
          <div key={c.title} style={{ background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ aspectRatio: "16 / 9", background: "var(--bg-1)", borderBottom: "1px solid var(--line-1)", padding: 16, overflow: "hidden" }}>
              <FakeWidget kind={i} colors={c.colors} />
            </div>
            <div style={{ padding: "16px 18px" }}>
              <div style={{ fontFamily: "var(--font-sans)", fontSize: 14.5, fontWeight: 600, color: "var(--fg-1)" }}>{c.title}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-4)", marginTop: 2, textTransform: "lowercase", letterSpacing: "0.04em" }}>{c.who}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FakeWidget({ kind, colors }: { kind: number; colors: string[] }) {
  const c0 = colors[0];
  const c1 = colors[1] || colors[0];
  const c2 = colors[2] || colors[0];
  if (kind === 0) {
    const bars = [38, 62, 48, 80, 56, 72];
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 18, fontWeight: 600, color: "var(--fg-1)" }}>$1.24M</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--success)" }}>+12.4%</span>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 6 }}>
          {bars.map((h, i) => (
            <div key={i} style={{ flex: 1, height: `${h}%`, background: i === bars.length - 1 ? c0 : "var(--surface-3)", borderRadius: 2 }} />
          ))}
        </div>
      </div>
    );
  }
  if (kind === 1) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, height: "100%" }}>
        <svg viewBox="0 0 100 100" style={{ width: 90, height: 90 }}>
          <circle cx="50" cy="50" r="40" fill="none" stroke="var(--surface-3)" strokeWidth="10" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={c0} strokeWidth="10" strokeDasharray="251" strokeDashoffset="55" strokeLinecap="round" transform="rotate(-90 50 50)" />
        </svg>
        <div>
          <div style={{ fontFamily: "var(--font-sans)", fontSize: 24, fontWeight: 600, color: "var(--fg-1)" }}>78</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)", marginTop: 2 }}>healthy · 412 accounts</div>
        </div>
      </div>
    );
  }
  if (kind === 2) {
    const items: [string, string][] = [["chen", "shipped auth migration"], ["priya", "blocked on stripe webhook"], ["dev", "PR #482 review needed"]];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "var(--font-sans)", fontSize: 11.5 }}>
        {items.map(([n, t], i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 18, height: 18, borderRadius: "50%", background: c0, opacity: 0.4 + i * 0.2, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600 }}>{n[0].toUpperCase()}</span>
            <span style={{ color: "var(--fg-1)", fontWeight: 500 }}>{n}</span>
            <span style={{ color: "var(--fg-3)", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === 3) {
    const svcs: [string, string][] = [["api", c2], ["edge", c2], ["db", c1], ["auth", c0], ["search", c2], ["billing", c2]];
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, height: "100%" }}>
        {svcs.map(([n, col], i) => (
          <div key={i} style={{ background: "var(--surface-2)", border: "1px solid var(--line-1)", borderRadius: 6, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: col }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{n}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === 4) {
    const rows: [string, string, string][] = [["seats × 24", "$456", "var(--fg-1)"], ["SSO add-on", "$120", "var(--fg-1)"], ["annual discount", "-$144", c0]];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", justifyContent: "center" }}>
        {rows.map(([l, v, col], i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-3)" }}>
            <span>{l}</span>
            <span style={{ color: col }}>{v}</span>
          </div>
        ))}
        <div style={{ borderTop: "1px solid var(--line-1)", paddingTop: 6, display: "flex", justifyContent: "space-between", fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600 }}>
          <span style={{ color: "var(--fg-1)" }}>total / mo</span>
          <span style={{ color: c0 }}>$432</span>
        </div>
      </div>
    );
  }
  const inc: [string, string][] = [["P1", "edge worker timeout in us-east"], ["P2", "stripe webhook lag (3m)"], ["P3", "logs slow on /metrics"]];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "var(--font-mono)", fontSize: 10.5 }}>
      {inc.map(([p, msg], i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", background: "var(--surface-2)", border: "1px solid var(--line-1)", borderRadius: 4 }}>
          <span style={{ color: i === 0 ? c0 : "var(--fg-3)", fontWeight: 600 }}>{p}</span>
          <span style={{ color: "var(--fg-2)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{msg}</span>
        </div>
      ))}
    </div>
  );
}

// ── Pricing (real Stripe, with the billing-cycle toggle) ─────────────────────
type Cycle = "monthly" | "yearly" | "lifetime";

function Pricing() {
  const signedIn = !!getJwt();
  const [cycle, setCycle] = useState<Cycle>("monthly");

  const proPlan: CheckoutPlan = cycle === "monthly" ? "pro_monthly" : cycle === "yearly" ? "pro_annual" : "lifetime";
  const proPrice = cycle === "monthly" ? "$8" : cycle === "yearly" ? "$79" : "$149";
  const proCadence =
    cycle === "monthly" ? "per month" : cycle === "yearly" ? "per year · ~$6.58/mo" : "one-time · yours forever";
  const proCta = cycle === "lifetime" ? "Get Lifetime" : "Go Pro";
  const proBadge = cycle === "lifetime" ? "best value" : cycle === "yearly" ? "save ~18%" : "most popular";

  return (
    <section
      id="pricing"
      style={{ maxWidth: MAX, margin: "0 auto", padding: "96px 32px", borderTop: "1px solid var(--line-1)", scrollMarginTop: 56 }}
    >
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <MBEyebrow style={{ marginBottom: 14 }}>Pricing</MBEyebrow>
        <h2 style={{ fontFamily: "var(--font-sans)", fontSize: 40, lineHeight: "44px", letterSpacing: "-0.025em", fontWeight: 600, color: "var(--fg-1)", margin: 0, textWrap: "balance" }}>
          Honest pricing. Real free tier.
        </h2>
        <p style={{ fontFamily: "var(--font-sans)", fontSize: 16, lineHeight: "24px", color: "var(--fg-2)", margin: "14px auto 0", maxWidth: 560 }}>
          No seat math, no AI-token meters, no "contact us" wall. Start free, upgrade when you outgrow one project — or buy it once and never think about it again.
        </p>

        <div style={{ marginTop: 28, display: "inline-flex", alignItems: "center", gap: 4, padding: 4, background: "var(--surface-1)", border: "1px solid var(--line-2)", borderRadius: 8 }}>
          {([
            { k: "monthly", label: "Monthly" },
            { k: "yearly", label: "Yearly", badge: "save ~18%" },
            { k: "lifetime", label: "Lifetime", badge: "pay once", accent: true },
          ] as const).map((opt) => {
            const on = cycle === opt.k;
            return (
              <button
                key={opt.k}
                onClick={() => setCycle(opt.k)}
                style={{
                  padding: "8px 14px",
                  background: on ? "var(--surface-3)" : "transparent",
                  color: on ? "var(--fg-1)" : "var(--fg-3)",
                  border: 0,
                  borderRadius: 6,
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 180ms",
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
                {"badge" in opt && opt.badge && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10.5,
                      padding: "2px 6px",
                      background: "accent" in opt && opt.accent ? "var(--accent-soft)" : "var(--surface-2)",
                      color: "accent" in opt && opt.accent ? "var(--accent-fg)" : "var(--fg-2)",
                      border: `1px solid ${"accent" in opt && opt.accent ? "var(--accent-line)" : "var(--line-2)"}`,
                      borderRadius: 4,
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-price-grid">
        <PriceCard
          name="Free"
          price="$0"
          cadence="free, forever"
          body="Build, remix, and connect. Perfect for personal tools and weekend projects."
          features={[
            "Unlimited widget generation & remix",
            "The Inbox for your renders",
            "1 project you can connect as an MCP",
            "Static data",
          ]}
          cta={
            <Link to={signedIn ? "/projects" : "/login"} style={{ display: "block" }}>
              <MBBtn variant="outline" style={{ width: "100%", justifyContent: "center" }}>
                {signedIn ? "Open your projects" : "Start free"}
              </MBBtn>
            </Link>
          }
        />
        <PriceCard
          highlight
          badge={proBadge}
          name="Pro"
          price={proPrice}
          cadence={proCadence}
          body="For builders shipping real, reusable tools — and sharing them."
          features={[
            "Everything in Free",
            "Unlimited projects",
            "Version history & restore",
            "Live data — bring your own API keys",
            "Publish public MCP endpoints + galleries",
            "Usage analytics",
            "Export any widget or project (.zip)",
          ]}
          cta={<CheckoutButton plan={proPlan} signedIn={signedIn} label={proCta} hero />}
        />
      </div>

      <div style={{ marginTop: 28, textAlign: "center", fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", textTransform: "lowercase", letterSpacing: "0.04em" }}>
        {cycle === "lifetime"
          ? "lifetime · pay once, yours forever · includes all future updates · secure checkout via stripe"
          : "no credit card to start · cancel anytime · secure checkout via stripe"}
      </div>
    </section>
  );
}

function CheckoutButton({
  plan,
  signedIn,
  label,
  hero = false,
}: {
  plan: CheckoutPlan;
  signedIn: boolean;
  label: string;
  hero?: boolean;
}) {
  const [state, setState] = useState<"idle" | "redirecting" | "error">("idle");
  const [err, setErr] = useState("");
  const onClick = async () => {
    if (!signedIn) {
      window.location.href = "/login";
      return;
    }
    setState("redirecting");
    setErr("");
    try {
      const { url } = await checkout(plan);
      window.location.href = url;
    } catch (e) {
      setState("error");
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div style={{ width: "100%" }}>
      <MBBtn
        variant={hero ? "primary" : "outline"}
        onClick={onClick}
        disabled={state === "redirecting"}
        style={{ width: "100%", justifyContent: "center" }}
      >
        {state === "redirecting" ? "Redirecting…" : label}
      </MBBtn>
      {state === "error" && (
        <div style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--danger)" }}>{err}</div>
      )}
    </div>
  );
}

function PriceCard({
  name,
  price,
  cadence,
  body,
  features,
  cta,
  highlight = false,
  badge,
}: {
  name: string;
  price: string;
  cadence: string;
  body: string;
  features: string[];
  cta: ReactNode;
  highlight?: boolean;
  badge?: string;
}) {
  return (
    <div
      style={{
        background: highlight ? "var(--surface-2)" : "var(--surface-1)",
        border: `1px solid ${highlight ? "var(--accent-line)" : "var(--line-2)"}`,
        borderRadius: 12,
        padding: 28,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        boxShadow: highlight ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(59,130,255,0.08)" : "none",
      }}
    >
      {badge && (
        <div style={{ position: "absolute", top: 16, right: 16 }}>
          <MBChip accent>{badge}</MBChip>
        </div>
      )}
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 600, color: "var(--fg-1)" }}>{name}</div>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 48, fontWeight: 600, letterSpacing: "-0.03em", color: "var(--fg-1)", lineHeight: 1 }}>{price}</span>
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-3)", marginTop: 8, letterSpacing: "0.02em" }}>{cadence}</div>
      </div>
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: "20px", color: "var(--fg-2)" }}>{body}</div>
      <div style={{ height: 1, background: "var(--line-1)", margin: "6px 0" }} />
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {features.map((f) => (
          <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--fg-2)" }}>
            <MBIcon name="check" size={14} color="var(--accent)" />
            {f}
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 8 }}>{cta}</div>
    </div>
  );
}

// ── FAQ ──────────────────────────────────────────────────────────────────────
function Faq() {
  const items: { q: string; a: string; more?: { to: string; label: string } }[] = [
    {
      q: "What is MCP, exactly?",
      a: "MCP — the Model Context Protocol — is an open standard for connecting AI assistants to tools and data. MCPBuild gives every project its own MCP server URL, so you can plug it into Claude, ChatGPT, Cursor, or any other MCP-aware client. You don't need to understand the protocol — just paste the URL into Claude or ChatGPT's connector settings and your tools are there.",
      more: { to: "/docs/endpoints", label: "Workshop vs project endpoints" },
    },
    {
      q: "I'm not an engineer — can I really use this for demos and prototypes?",
      a: "Yes, that's the design target. You describe what you want in a chat with Claude or ChatGPT (\"a dashboard that shows me orders with a Cancel button per row\") — the AI writes the React for you, MCPBuild hosts it, and the same widget renders inline in Claude, ChatGPT, and on your project's web page. No code, no deploy step. You get a shareable link and an MCP URL the same minute.",
      more: { to: "/docs/quickstart", label: "Quickstart — your first project in 5 min" },
    },
    {
      q: "What's the “workshop” MCP vs the project MCP, and which one do I connect?",
      a: "Connect both — they do different jobs. The workshop MCP (mcpbuild.app/mcp) is where you BUILD: it gives you the dynamic_widget, project, and remix_widget tools so the AI can author and save tools for you. The project MCP (mcpbuild.app/mcp/<your-project>) is where the tools you built actually live and get CALLED by the model. Rule of thumb: connect the workshop in your day-to-day Claude/ChatGPT so you can prototype anywhere; connect a specific project endpoint when you (or someone you're demoing to) wants to USE that project's tools without seeing the build surface.",
      more: { to: "/docs/endpoints", label: "Workshop vs project endpoints" },
    },
    {
      q: "How do I demo my project to someone without making them learn anything?",
      a: "Three options. (1) Send them the project's public share link — they get the widget rendered in a webpage, no connector needed. (2) Have them connect your project's MCP URL in their own Claude/ChatGPT and just chat naturally. To make natural chat actually trigger your tools, add a few “demo prompts” on the Triggers tab — phrases a real user would say, like “cancel order A-100” or “show me this week's pipeline.” Those phrases are appended to the tool's description so the model picks the right tool without you having to coach the audience. (3) On the project page, click any of the saved demo-prompt pills to copy a working prompt to your clipboard.",
      more: { to: "/docs/triggers", label: "Trigger phrases & natural demos" },
    },
    {
      q: "What are trigger phrases, and why does every tool need them?",
      a: "A trigger phrase is a short sentence a real user would say to invoke your tool — for example, “cancel order A-100”, “refund the customer named Alice”, or “show me this week's pipeline.” You add them on each tool's Triggers tab. Two things happen: (1) they're appended to that tool's MCP description so Claude and ChatGPT pick the right tool when someone phrases the request naturally — you no longer need to tell the audience the tool's name; (2) each phrase becomes a one-click “Try this prompt” pill on the project page so anyone giving a demo can copy a working prompt to their clipboard. Rule of thumb: 3–5 phrases per tool, written the way a non-technical user would actually speak.",
      more: { to: "/docs/triggers", label: "Trigger phrases & natural demos" },
    },
    {
      q: "I built a widget but it's not on my project page — where did it go?",
      a: "It's in your Inbox. By default, prompts like “use dynamic_widget for a chart…” drop the widget into a 24-hour ephemeral Inbox so you can iterate without polluting your projects. To save it into a named project in the same step, tell the AI which project — e.g. “use dynamic_widget with projectName: 'Orders' …” — and it auto-saves the widget as a callable tool on /mcp/orders, no second step required. If a widget is already in the Inbox, open it and click Save to project to keep it.",
      more: { to: "/docs/troubleshooting", label: "Troubleshooting — missing widget" },
    },
    {
      q: "My widget shows up in Claude but my project page is blank — what happened?",
      a: "It used to fail silently because the page didn't surface widget runtime errors. We fixed that — broken widgets now print their actual JavaScript error inline in the iframe instead of going blank. If you see a stack trace there, copy it back into the same chat and ask the AI to fix it (e.g., “the widget threw `Cannot read properties of undefined (reading 'map')` — fix it”). Usually it's a one-shot remix_widget call.",
      more: { to: "/docs/troubleshooting", label: "Troubleshooting — blank iframe" },
    },
    {
      q: "Do I need to know React?",
      a: "No. The AI writes the React for you. You refine it by chat (\"make the chart darker\", \"sort by date\", \"add a column for the customer email\") and never touch code — but every widget is plain TSX you can read, edit, or download if you want to.",
      more: { to: "/docs/widgets", label: "Building widgets with dynamic_widget" },
    },
    {
      q: "How is this different from a Claude artifact or a ChatGPT canvas?",
      a: "Artifacts and canvases live inside one conversation — they vanish when you start a new chat. MCPBuild widgets persist as projects, version every change, get their own MCP endpoint, and work across both chat clients — same widget, no rewrite. You can also share them via a public URL or invite teammates without anyone having to copy code around.",
      more: { to: "/docs/sharing", label: "Sharing & teams" },
    },
    {
      q: "Where does my data live, and is it safe to demo against a real API?",
      a: "Widgets and projects live on AWS, encrypted at rest. If a widget needs to fetch from a real API (say, your CRM), project secrets are KMS-encrypted, scoped to that one project's MCP endpoint, and injected server-side at fetch time — the model never sees them in plaintext. For demos, prefer pasting fake data into a widget; for real demos, the secrets+allowlist flow keeps your keys off the screen.",
      more: { to: "/docs/live-data", label: "Live data, secrets & fetchData" },
    },
    {
      q: "Which models can build widgets?",
      a: "Claude (Sonnet, Opus) and ChatGPT (GPT-4-class models). MCPBuild never runs inference itself — the model on the other end of the chat writes the React, and we validate, bundle, and host it. That means we pass through to whatever account you already pay for — no extra LLM bill.",
    },
    {
      q: "Can I self-host?",
      a: "Not today — it's a hosted service. Your widgets are portable, though: download any widget or a whole project as a self-contained .zip and re-render it anywhere.",
    },
  ];
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      style={{ maxWidth: 860, margin: "0 auto", padding: "96px 32px", borderTop: "1px solid var(--line-1)", scrollMarginTop: 56 }}
    >
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <MBEyebrow style={{ marginBottom: 14 }}>FAQ</MBEyebrow>
        <h2 style={{ fontFamily: "var(--font-sans)", fontSize: 36, lineHeight: "40px", letterSpacing: "-0.02em", fontWeight: 600, color: "var(--fg-1)", margin: 0 }}>
          Questions, answered straight.
        </h2>
      </div>

      <div style={{ border: "1px solid var(--line-2)", borderRadius: 10, overflow: "hidden", background: "var(--surface-1)" }}>
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={i} style={{ borderTop: i ? "1px solid var(--line-1)" : 0 }}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "20px 24px",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  fontFamily: "var(--font-sans)",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "var(--fg-1)",
                  letterSpacing: "-0.01em",
                }}
              >
                {it.q}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    color: "var(--fg-3)",
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                >
                  <MBIcon name="chevron" size={14} />
                </span>
              </button>
              <div style={{ maxHeight: isOpen ? 600 : 0, overflow: "hidden", transition: "max-height 240ms cubic-bezier(0.2, 0.8, 0.2, 1)" }}>
                <div style={{ padding: "0 24px 20px", fontFamily: "var(--font-sans)", fontSize: 14.5, lineHeight: "22px", color: "var(--fg-2)", maxWidth: 680 }}>
                  {it.a}
                  {it.more && (
                    <div style={{ marginTop: 12 }}>
                      <Link
                        to={it.more.to}
                        style={{
                          fontFamily: "var(--font-sans)",
                          fontSize: 13,
                          color: "var(--accent-fg)",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        {it.more.label} →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Final CTA ────────────────────────────────────────────────────────────────
function FinalCta() {
  const signedIn = !!getJwt();
  return (
    <section style={{ borderTop: "1px solid var(--line-1)", position: "relative", overflow: "hidden" }}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 50% 60% at 50% 50%, #000 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 50% 60% at 50% 50%, #000 30%, transparent 75%)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          height: 240,
          background: "radial-gradient(ellipse at center, rgba(59,130,255,0.10), transparent 65%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", maxWidth: 860, margin: "0 auto", padding: "112px 32px", textAlign: "center" }}>
        <h2 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: "clamp(36px, 6vw, 56px)", lineHeight: 1.07, letterSpacing: "-0.03em", fontWeight: 600, color: "var(--fg-1)", textWrap: "balance" }}>
          Stop pasting screenshots <br />
          <span style={{ color: "var(--fg-3)" }}>into your AI chats.</span>
        </h2>
        <p style={{ margin: "20px auto 0", maxWidth: 520, fontFamily: "var(--font-sans)", fontSize: 17, lineHeight: "26px", color: "var(--fg-2)" }}>
          Your AI already knows what to build. MCPBuild gives it a place to put it — and you a way to use it again tomorrow.
        </p>
        <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to={signedIn ? "/projects" : "/login"}>
            <MBBtn variant="primary" size="lg" icon={<MBIcon name="arrow" size={15} />}>
              {signedIn ? "Open your projects" : "Start free"}
            </MBBtn>
          </Link>
          <a href="#how">
            <MBBtn variant="outline" size="lg">See how it works</MBBtn>
          </a>
        </div>
        <div style={{ marginTop: 24, fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", letterSpacing: "0.04em", textTransform: "lowercase" }}>
          1 project free · no credit card · ships in 60 seconds
        </div>
      </div>
    </section>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const cols: [string, [string, string, boolean][]][] = [
    ["Product", [["Features", "#features", false], ["Pricing", "#pricing", false], ["FAQ", "#faq", false]]],
    ["Build with", [["Claude", "https://claude.ai", true], ["ChatGPT", "https://chatgpt.com", true], ["Cursor", "https://cursor.com", true]]],
    ["Resources", [["Docs", "/docs", false], ["MCP spec", "https://modelcontextprotocol.io", true], ["Privacy", "/privacy", false], ["Terms", "/terms", false]]],
  ];
  return (
    <footer style={{ borderTop: "1px solid var(--line-1)" }}>
      <div style={{ maxWidth: MAX, margin: "0 auto", padding: "64px 32px 48px" }}>
        <div className="mb-footer-grid" style={{ marginBottom: 48 }}>
          <div>
            <MBWordmark size={18} />
            <p style={{ marginTop: 14, fontFamily: "var(--font-sans)", fontSize: 13.5, lineHeight: "20px", color: "var(--fg-3)", maxWidth: 280 }}>
              Turn any conversation into a connectable tool. Built for Claude, ChatGPT, and the open MCP ecosystem.
            </p>
            <div style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)" }}>
              <MBDot color="var(--success)" pulse /> all systems normal
            </div>
          </div>
          {cols.map(([h, links]) => (
            <div key={h}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-4)", marginBottom: 14 }}>{h}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {links.map(([label, href, ext]) =>
                  ext ? (
                    <a key={label} href={href} target="_blank" rel="noreferrer" style={{ fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--fg-2)" }}>{label}</a>
                  ) : href.startsWith("#") ? (
                    <a key={label} href={href} style={{ fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--fg-2)" }}>{label}</a>
                  ) : (
                    <Link key={label} to={href} style={{ fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--fg-2)" }}>{label}</Link>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 24, borderTop: "1px solid var(--line-1)", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)" }}>© 2026 mcpbuild · made by martzmakes</div>
          <a href="https://martzmakes.com" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)" }}>
            built by <MartzmakesWordmark size={12} />
          </a>
        </div>
      </div>
    </footer>
  );
}
